import { merchantName } from '../features/agency/merchantName';
import { SourceFilters, Provenance } from '../features/agency/ui';
import { Fragment, useState, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useAssignmentList,
  ASSIGNMENT_STATUS_LABELS,
  STATUS_FILTER_OPTIONS,
  TASK_TYPE_OPTIONS,
  PLATFORM_OPTIONS,
  APPLE_ID_MATCH_RESULT_LABELS,
  APPLE_ID_MATCH_RESULT_OPTIONS,
  ASSIGNMENT_SORT_ORDER_OPTIONS,
} from '../features/assignments';
import type { AppleIdMatchResult, AssignmentDTO, AssignmentStatus } from '../features/assignments';
import { TASK_STATUS_LABELS } from '../features/tasks';
import type { TaskStatus } from '../features/tasks';
import { ConfirmDialog } from '../components/feedback/ConfirmDialog';
import { TaskEvidenceGallery, TaskEvidenceThumbnail } from '../components/media/TaskEvidenceGallery';
import { EVIDENCE_THUMBNAIL_OPTIONS, buildEvidenceThumbnailUrl } from '../lib/upload/cosThumbnail';
import { parseSubmissionEvidence } from '../features/assignments/utils/submissionEvidence';
import { messageReviewReasonCodesLabel } from '../features/assignments/utils/messageReviewLabels';
import { hasAdminPermission } from '../lib/adminPermissions';
import {
  resolveAdminImageOriginalUrl,
  resolveAdminImageThumbnailUrl,
} from '../lib/upload/cos';
import { useAuthStore } from '../store/auth.store';
import { useMarketConfigs } from '../features/settings';

const STATUS_STYLES: Record<AssignmentStatus, string> = {
  assigned: 'bg-blue-50 text-blue-700',
  submitted: 'bg-amber-50 text-amber-700',
  approved: 'bg-green-50 text-green-700',
  rejected: 'bg-red-50 text-red-700',
  settled: 'bg-green-50 text-green-700',
  expired: 'bg-gray-100 text-gray-600',
};

const TASK_TYPE_BADGE: Record<string, string> = {
  download: 'bg-indigo-50 text-indigo-700',
  normal: 'bg-gray-100 text-gray-600',
  other_normal: 'bg-sky-100 text-sky-700',
  message_send: 'bg-sky-50 text-sky-700',
};

const PLATFORM_BADGE: Record<string, string> = {
  ios: 'bg-blue-50 text-blue-700',
  android: 'bg-emerald-50 text-emerald-700',
  web: 'bg-gray-100 text-gray-600',
  all: 'bg-gray-100 text-gray-600',
};

const TASK_STATUS_BADGE: Record<TaskStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  active: 'bg-blue-50 text-blue-700',
  paused: 'bg-amber-50 text-amber-700',
  archived: 'bg-gray-100 text-gray-500',
};

const TASK_TYPE_LABEL: Record<string, string> = {
  download: '下载',
  normal: '普通',
  other_normal: '其他普通',
  message_send: '短信',
};

const PLATFORM_LABEL: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
  all: '全平台',
};

const APPLE_ID_MATCH_BADGE: Record<AppleIdMatchResult, string> = {
  unchecked: 'bg-gray-100 text-gray-600',
  checking: 'bg-amber-50 text-amber-700',
  matched: 'bg-green-50 text-green-700',
  mismatch: 'bg-red-50 text-red-700',
  failed: 'bg-amber-50 text-amber-700',
};

interface ViewTab {
  key: string;
  label: string;
  subtitle: string;
  emptyText: string;
}

const VIEW_TABS: ViewTab[] = [
  { key: '', label: '全部任务', subtitle: '审核用户提交的任务完成证据，支持按状态、任务类型和平台筛选。', emptyText: '暂无数据' },
  { key: 'download', label: '下载任务', subtitle: '审核用户提交的应用下载完成证据，支持按状态和平台筛选。', emptyText: '暂无下载任务的审核数据' },
  { key: 'other_normal', label: '其他普通任务', subtitle: '审核操作流程截图，无需校验 Apple ID。', emptyText: '暂无其他普通任务的审核数据' },
  { key: 'normal', label: '普通任务', subtitle: '审核用户提交的普通任务完成证据，支持按状态和平台筛选。', emptyText: '暂无普通任务的审核数据' },
  { key: 'message_send', label: '短信代发', subtitle: '审核用户提交的短信代发证据，支持按状态和平台筛选。', emptyText: '暂无短信代发的审核数据' },
];

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function EvidenceCell({ raw }: { raw: string }) {
  const evidence = parseSubmissionEvidence(raw || '');
  const imagePreviews = evidence.imageUrls.map((src) => {
    const thumbSrc = resolveAdminImageThumbnailUrl(src, EVIDENCE_THUMBNAIL_OPTIONS);
    const fullSrc = resolveAdminImageOriginalUrl(src);
    return thumbSrc ? { src: thumbSrc, fullSrc } : null;
  }).filter((item): item is { src: string; fullSrc: string } => item !== null);
  const hasImagePreview = imagePreviews.length > 0;
  const text = evidence.description || (!hasImagePreview ? raw : '');

  if (!raw) {
    return <span className="text-xs text-gray-300">-</span>;
  }

  return (
    <div className="min-w-[160px] max-w-[240px] space-y-1.5">
      {text && (
        <p className="line-clamp-2 break-words text-xs text-gray-600" title={text}>
          {text}
        </p>
      )}
      {hasImagePreview && (
        <TaskEvidenceGallery>
          {imagePreviews.map((image, index) => (
            <TaskEvidenceThumbnail key={`${image.fullSrc}-${index}`} {...image} label={`提交截图 ${index + 1}`} />
          ))}
        </TaskEvidenceGallery>
      )}
      {!hasImagePreview && evidence.legacyImagePaths.length > 0 && (
        <p className="line-clamp-2 break-all font-mono text-xs text-amber-700" title={evidence.legacyImagePaths.join('\n')}>
          {evidence.legacyImagePaths.join('\n')}
        </p>
      )}
    </div>
  );
}

function messageReviewStatusLabel(value?: string, reasonCodes?: string[] | null): string {
  if (reasonCodes?.includes('evidence_reused')) return '系统已驳回';
  switch (value) {
    case 'queued':
      return 'AI审核中';
    case 'running':
      return 'AI审核中';
    case 'succeeded':
      return 'AI已完成';
    case 'manual_required':
      return '人工复核';
    case 'failed':
      return 'AI失败';
    case 'not_started':
      return '未开始';
    default:
      return value || '-';
  }
}

function assignmentMessageReviewReasonLabel(item: AssignmentDTO): string {
  const reviewReasonCodes = (item.message_review?.reason_codes ?? []).filter(Boolean);
  const evidenceReasonCodes = (item.message_evidence ?? [])
    .map((entry) => entry.reason_code)
    .filter((code): code is string => Boolean(code));
  if (
    reviewReasonCodes.length === 0 &&
    evidenceReasonCodes.length === 0 &&
    item.message_review?.job_status === 'succeeded' &&
    item.message_review?.recommendation === 'pass_candidate'
  ) {
    return '成功';
  }
  return messageReviewReasonCodesLabel(
    reviewReasonCodes.length > 0 ? reviewReasonCodes : evidenceReasonCodes,
    '-',
  );
}

function MessageEvidenceThumb({ evidence, onRetry }: { evidence: NonNullable<AssignmentDTO['message_evidence']>[number]; onRetry: () => void }) {
  const fullSrc = evidence.url;
  return <TaskEvidenceThumbnail
    src={fullSrc ? buildEvidenceThumbnailUrl(fullSrc) : null}
    fullSrc={fullSrc}
    error={!fullSrc}
    unavailable={evidence.content_available === false}
    onRetry={onRetry}
    label={`提交截图 ${evidence.position_no}`}
  />;
}

function MessageAssignmentEvidenceCell({ item, onRetry }: { item: AssignmentDTO; onRetry: () => void }) {
  const evidence = item.message_evidence ?? [];
  return (
    <div className="min-w-[260px] max-w-[420px] space-y-2">
      <div className="space-y-1">
        <p className="break-all text-xs text-gray-700">
          任务目标号码：{item.message_task?.target_account || '-'}
        </p>
        <p className="whitespace-pre-wrap break-words text-xs text-gray-600">
          任务要求正文：{item.message_task?.target_content || '-'}
        </p>
        <p className="text-xs text-gray-500">
          审核状态：{messageReviewStatusLabel(item.message_review?.job_status, item.message_review?.reason_codes)}
        </p>
        <p className="text-xs text-red-600">
          具体原因：{assignmentMessageReviewReasonLabel(item)}
        </p>
      </div>
      {evidence.length > 0 ? (
        <TaskEvidenceGallery>
          {evidence.map((entry) => (
            <MessageEvidenceThumb key={entry.id} evidence={entry} onRetry={onRetry} />
          ))}
        </TaskEvidenceGallery>
      ) : (
        <span className="text-xs text-gray-300">暂无截图</span>
      )}
    </div>
  );
}

type DialogState =
  | { type: 'closed' }
  | { type: 'approve'; assignmentIds: number[] }
  | { type: 'reject'; assignmentIds: number[] }
  | { type: 'delete'; assignmentIds: number[] };

const selectCls = 'h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50';
const PAGE_SIZE_OPTIONS = [20, 50, 100, 500, 1000];
const ASSIGNMENT_TABLE_COLUMNS = 15;

interface AssignmentGroup {
  taskId: number;
  taskTitle: string;
  taskStatus?: string;
  items: AssignmentDTO[];
}

function TaskStatusBadge({ status }: { status?: string }) {
  if (!status) return null;
  const taskStatus = status as TaskStatus;
  return (
    <span className={`inline-block shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${TASK_STATUS_BADGE[taskStatus] ?? 'bg-gray-100 text-gray-600'}`}>
      {TASK_STATUS_LABELS[taskStatus] ?? status}
    </span>
  );
}

function AppleIdMatchBadge({ result }: { result?: AppleIdMatchResult }) {
  const normalized = result ?? 'unchecked';
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${APPLE_ID_MATCH_BADGE[normalized] ?? APPLE_ID_MATCH_BADGE.unchecked}`}>
      {APPLE_ID_MATCH_RESULT_LABELS[normalized] ?? normalized}
    </span>
  );
}

function canDeleteAssignmentRecord(item: AssignmentDTO): boolean {
  if (item.status === 'approved' || item.status === 'settled') return false;
  if (item.task_type === 'message_send') return item.status === 'assigned';
  return true;
}

export function AssignmentsPage() {
  const user = useAuthStore((s) => s.user);
  const {
    items, total, page, pageSize, totalPages,
    agentName, marketId, status, taskId, userId, userKeyword, taskKeyword, taskType, platform, appleIdMatchResult, order,
    loading, error, reviewing, deleting, refreshImages,
    setAgentName, setPage, setPageSize, setMarketId, setStatus, setTaskId, setUserId, setUserKeyword, setTaskKeyword, setTaskType, setPlatform, setAppleIdMatchResult, setOrder, resetFilters, reload, review, reviewMany, deleteMany,
  } = useAssignmentList();
  const { items: marketConfigs } = useMarketConfigs();

  const [dialog, setDialog] = useState<DialogState>({ type: 'closed' });
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [groupByTask, setGroupByTask] = useState(true);
  const canReviewAssignments = hasAdminPermission(user, 'assignments.review');
  const canDeleteAssignments = hasAdminPermission(user, 'assignments');

  const submittedIds = useMemo(
    () => items.filter((item) => item.status === 'submitted').map((item) => item.id),
    [items],
  );
  const assignmentGroups = useMemo(() => {
    const groups = new Map<number, AssignmentGroup>();
    for (const item of items) {
      const existing = groups.get(item.task_id);
      if (existing) {
        existing.items.push(item);
        continue;
      }
      groups.set(item.task_id, {
        taskId: item.task_id,
        taskTitle: item.task_title,
        taskStatus: item.task_status,
        items: [item],
      });
    }
    return Array.from(groups.values());
  }, [items]);
  const reviewableIds = useMemo(
    () => (canReviewAssignments ? submittedIds : []),
    [canReviewAssignments, submittedIds],
  );
  const deletableIds = useMemo(
    () => (canDeleteAssignments
      ? items.filter(canDeleteAssignmentRecord).map((item) => item.id)
      : []),
    [canDeleteAssignments, items],
  );
  const selectableIds = useMemo(
    () => Array.from(new Set([...reviewableIds, ...deletableIds])),
    [deletableIds, reviewableIds],
  );
  const selectedSubmittedIds = useMemo(
    () => selectedIds.filter((id) => reviewableIds.includes(id)),
    [reviewableIds, selectedIds],
  );
  const selectedDeletableIds = useMemo(
    () => selectedIds.filter((id) => deletableIds.includes(id)),
    [deletableIds, selectedIds],
  );
  const allSelectableSelected = selectableIds.length > 0 && selectedIds.filter((id) => selectableIds.includes(id)).length === selectableIds.length;

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => selectableIds.includes(id)));
  }, [selectableIds]);

  const toggleSelected = useCallback((assignmentId: number) => {
    setSelectedIds((prev) => (
      prev.includes(assignmentId)
        ? prev.filter((id) => id !== assignmentId)
        : [...prev, assignmentId]
    ));
  }, []);

  const toggleCurrentPage = useCallback(() => {
    setSelectedIds((prev) => {
      if (allSelectableSelected) {
        return prev.filter((id) => !selectableIds.includes(id));
      }
      const next = new Set(prev);
      selectableIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  }, [allSelectableSelected, selectableIds]);

  const handleConfirm = useCallback((note: string) => {
    if (dialog.type === 'closed') return;
    if (dialog.type === 'delete') {
      deleteMany(dialog.assignmentIds);
      setSelectedIds([]);
      setDialog({ type: 'closed' });
      return;
    }
    const action = dialog.type === 'approve' ? 'approve' : 'reject';
    if (dialog.assignmentIds.length === 1) {
      review(dialog.assignmentIds[0], action, note);
    } else {
      reviewMany(dialog.assignmentIds, action, note);
    }
    setSelectedIds([]);
    setDialog({ type: 'closed' });
  }, [deleteMany, dialog, review, reviewMany]);

  const handleCancel = useCallback(() => {
    setDialog({ type: 'closed' });
  }, []);

  const isTypedView = taskType === 'download' || taskType === 'normal' || taskType === 'other_normal' || taskType === 'message_send';
  const activeTab = VIEW_TABS.find((t) => t.key === taskType) ?? VIEW_TABS[0];
  const renderAssignmentRow = (item: AssignmentDTO) => (
    <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 align-top">
        <input
          type="checkbox"
          checked={selectedIds.includes(item.id)}
          disabled={!selectableIds.includes(item.id) || reviewing || deleting}
          onChange={() => toggleSelected(item.id)}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50 disabled:opacity-30"
        />
      </td>
      <td className="px-4 py-3 text-gray-900 max-w-[220px]" title={item.task_title}>
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate">{item.task_title}<Provenance item={{...item,claim_revision_id:item.claim_snapshot?.revision_id}}/>{item.claim_snapshot?.reward_amount_minor!=null&&<small className="block text-gray-500">领取奖励 ¥{(item.claim_snapshot.reward_amount_minor/100).toFixed(2)}</small>}</span>
          <TaskStatusBadge status={item.task_status} />
        </div>
      </td>
      <td className="px-4 py-3 text-gray-700">
        <div className="w-40 whitespace-normal break-words" title={merchantName(item)}>{merchantName(item)}</div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          {item.task_type && (
            <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${TASK_TYPE_BADGE[item.task_type] ?? 'bg-gray-100 text-gray-600'}`}>
              {TASK_TYPE_LABEL[item.task_type] ?? item.task_type}
            </span>
          )}
          {item.platform && (
            <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${PLATFORM_BADGE[item.platform] ?? 'bg-gray-100 text-gray-600'}`}>
              {PLATFORM_LABEL[item.platform] ?? item.platform}
            </span>
          )}
          <span className="inline-block rounded px-1.5 py-0.5 text-xs font-medium bg-slate-100 text-slate-600">
            {item.task_market_id || 'CN'}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 max-w-[180px] text-gray-600">
        <span className="block truncate" title={item.query_keyword || undefined}>{item.query_keyword || '—'}</span>
      </td>
      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
        <div>{item.user_nickname || '-'}</div>
        {item.user_email && (
          <div className="text-xs text-gray-400">{item.user_email}</div>
        )}
        <div className="max-w-[260px] whitespace-normal break-all text-xs text-gray-500">
          {item.task_type === 'other_normal' ? 'Apple ID：不适用' : `绑定 Apple ID：${item.user_apple_account || '未绑定'}`}
        </div>
        <div className="text-xs text-gray-400">
          App 版本 <span className="font-mono tabular-nums">{item.latest_app_version || '-'}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-gray-600">
        {item.agent_portal_task_id&&!hasAdminPermission(user,'agent_evidence.original.view') ? <Link className="text-xs text-primary" to={`/assignments/${item.id}`}>查看审核详情</Link> : item.task_type === 'message_send' ? <MessageAssignmentEvidenceCell item={item} onRetry={refreshImages} /> : <EvidenceCell raw={item.evidence} />}
      </td>
      <td className="px-4 py-3 text-center whitespace-nowrap">
        {item.task_type === 'other_normal' ? <span className="text-xs text-gray-400">不适用</span> : <AppleIdMatchBadge result={item.apple_id_match_result} />}
      </td>
      <td className="px-4 py-3 text-center">
        <span
          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
            STATUS_STYLES[item.status as AssignmentStatus] ?? 'bg-gray-100 text-gray-600'
          }`}
        >
          {ASSIGNMENT_STATUS_LABELS[item.status as AssignmentStatus] ?? item.status}
        </span>
      </td>
      <td className="px-4 py-3 max-w-[180px]">
        {item.review_note ? (
          <span
            className={`text-xs truncate block ${item.status === 'rejected' ? 'text-red-600' : 'text-gray-500'}`}
            title={item.review_note}
          >
            {item.review_note}
          </span>
        ) : (
          <span className="text-xs text-gray-300">-</span>
        )}
      </td>
      <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">
        {formatDateTime(item.submitted_at)}
      </td>
      <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">
        <span className="font-mono" title={item.assign_ip || undefined}>
          {item.assign_ip || '-'}
        </span>
      </td>
      <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">
        <span className="font-mono" title={item.submit_ip || undefined}>
          {item.submit_ip || '-'}
        </span>
      </td>
      <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">
        {formatDateTime(item.reviewed_at)}
      </td>
      <td className="px-4 py-3 text-center whitespace-nowrap">
        <div className="flex items-center justify-center gap-2">
          <Link
            to={`/assignments/${item.id}`}
            className="text-sm text-primary hover:underline"
          >
            详情
          </Link>
          {canReviewAssignments && item.status === 'submitted' && (
            <>
              <button
                onClick={() => setDialog({ type: 'approve', assignmentIds: [item.id] })}
                disabled={reviewing}
                className="text-sm text-green-600 hover:text-green-700 disabled:opacity-40 transition-colors"
              >
                通过
              </button>
              <button
                onClick={() => setDialog({ type: 'reject', assignmentIds: [item.id] })}
                disabled={reviewing}
                className="text-sm text-red-500 hover:text-red-600 disabled:opacity-40 transition-colors"
              >
                拒绝
              </button>
            </>
          )}
          {canDeleteAssignments && canDeleteAssignmentRecord(item) && (
            <button
              onClick={() => setDialog({ type: 'delete', assignmentIds: [item.id] })}
              disabled={deleting}
              className="text-sm text-red-500 hover:text-red-600 disabled:opacity-40 transition-colors"
            >
              删除
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <SourceFilters />
        <input
          type="text"
          aria-label="商户名称"
          placeholder="商户名称（模糊查询）"
          value={agentName}
          onChange={(e) => setAgentName(e.target.value)}
          className="h-9 w-48 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
      </div>
      {/* View tabs */}
      <div className="flex items-center gap-6 border-b border-gray-200">
        {VIEW_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setTaskType(tab.key)}
            className={`pb-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              taskType === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <p className="text-sm text-gray-500">{activeTab.subtitle}</p>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={selectCls}
        >
          {STATUS_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select
          value={marketId}
          onChange={(e) => setMarketId(e.target.value)}
          className={selectCls}
        >
          <option value="">全部市场</option>
          {marketConfigs.map((market) => (
            <option key={market.market_id} value={market.market_id}>
              {market.market_id} · {market.name}
            </option>
          ))}
        </select>

        {!isTypedView && (
          <select
            value={taskType}
            onChange={(e) => setTaskType(e.target.value)}
            className={selectCls}
          >
            {TASK_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}

        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          className={selectCls}
        >
          {PLATFORM_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select
          value={appleIdMatchResult}
          onChange={(e) => setAppleIdMatchResult(e.target.value)}
          className={selectCls}
        >
          {APPLE_ID_MATCH_RESULT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setGroupByTask((value) => !value)}
          className={`h-9 rounded-md border px-3 text-sm font-medium transition-colors ${
            groupByTask
              ? 'border-primary bg-primary/5 text-primary'
              : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          按任务归类
        </button>

        <input
          type="text"
          value={taskId}
          onChange={(e) => setTaskId(e.target.value.replace(/\D/g, ''))}
          placeholder="任务 ID"
          className="h-9 w-28 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />

        <input
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.target.value.replace(/\D/g, ''))}
          placeholder="用户 ID"
          className="h-9 w-28 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />

        <input
          type="text"
          value={userKeyword}
          onChange={(e) => setUserKeyword(e.target.value)}
          placeholder="会员账号/昵称"
          className="h-9 w-40 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />

        <input
          type="text"
          value={taskKeyword}
          onChange={(e) => setTaskKeyword(e.target.value)}
          placeholder="任务名称"
          className="h-9 w-40 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />

        <select
          value={order}
          onChange={(e) => setOrder(e.target.value as typeof order)}
          className={selectCls}
        >
          {ASSIGNMENT_SORT_ORDER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <button
          onClick={resetFilters}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          重置
        </button>
        <button
          onClick={reload}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          刷新
        </button>

        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className={selectCls}
        >
          {PAGE_SIZE_OPTIONS.map((value) => (
            <option key={value} value={value}>每页 {value} 条</option>
          ))}
        </select>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <span className="text-amber-800">已选择 {selectedIds.length} 条审核记录</span>
          <div className="flex items-center gap-2">
            {canReviewAssignments && (
              <>
                <button
                  onClick={() => setDialog({ type: 'approve', assignmentIds: selectedSubmittedIds })}
                  disabled={reviewing || selectedSubmittedIds.length === 0}
                  className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-40"
                >
                  批量通过
                </button>
                <button
                  onClick={() => setDialog({ type: 'reject', assignmentIds: selectedSubmittedIds })}
                  disabled={reviewing || selectedSubmittedIds.length === 0}
                  className="rounded-md bg-red-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-40"
                >
                  批量拒绝
                </button>
              </>
            )}
            {canDeleteAssignments && (
              <button
                onClick={() => setDialog({ type: 'delete', assignmentIds: selectedDeletableIds })}
                disabled={deleting || selectedDeletableIds.length === 0}
                className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
              >
                批量删除
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="py-10 text-center">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button onClick={reload} className="text-sm text-primary hover:underline">重试</button>
        </div>
      )}

      {/* Loading */}
      {loading && !error && (
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          {items.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-400">{activeTab.emptyText}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1800px] text-sm">
                <thead className="whitespace-nowrap">
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="w-10 px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={allSelectableSelected}
                        disabled={selectableIds.length === 0}
                        onChange={toggleCurrentPage}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50 disabled:opacity-40"
                      />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">任务</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">商户名称</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">类型 / 平台</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" title="本次领取使用的关键词，不代表页面加载成功">关键词</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">用户</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">证据</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Apple ID</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">审核备注</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">提交时间</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">领取 IP</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">做任务 IP</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">审核时间</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {groupByTask ? assignmentGroups.map((group) => {
                    const submittedCount = group.items.filter((item) => item.status === 'submitted').length;
                    const userCount = new Set(group.items.map((item) => item.user_id)).size;
                    return (
                      <Fragment key={group.taskId}>
                        <tr className="border-b border-gray-200 bg-gray-50/80">
                          <td colSpan={ASSIGNMENT_TABLE_COLUMNS} className="px-4 py-2">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div className="flex min-w-0 items-center gap-2">
                                <Link
                                  to={`/tasks/${group.taskId}`}
                                  className="max-w-[360px] truncate text-sm font-medium text-gray-900 hover:text-primary"
                                  title={group.taskTitle}
                                >
                                  #{group.taskId} {group.taskTitle || '未命名任务'}
                                </Link>
                                <TaskStatusBadge status={group.taskStatus} />
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span>提交 {group.items.length} 条</span>
                                <span>待审 {submittedCount} 条</span>
                                <span>用户 {userCount} 人</span>
                                <button
                                  type="button"
                                  onClick={() => setTaskId(String(group.taskId))}
                                  className="font-medium text-primary hover:text-primary/80"
                                >
                                  只看此任务
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                        {group.items.map(renderAssignmentRow)}
                      </Fragment>
                    );
                  }) : items.map(renderAssignmentRow)}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between pt-2 text-sm text-gray-500">
            <span>共 {total} 条</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                上一页
              </button>
              <span className="text-sm tabular-nums">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                下一页
              </button>
            </div>
          </div>
        </>
      )}

      {/* Approve dialog */}
      <ConfirmDialog
        open={dialog.type === 'approve'}
        title="确认通过审核"
        description="通过后将触发奖励结算，此操作不可撤销。"
        confirmLabel="通过"
        variant="primary"
        loading={reviewing}
        noteLabel="备注（可选）"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {/* Reject dialog */}
      <ConfirmDialog
        open={dialog.type === 'reject'}
        title="确认拒绝"
        description="拒绝后用户需重新提交，请填写原因。"
        confirmLabel="拒绝"
        variant="danger"
        loading={reviewing}
        noteLabel="拒绝原因"
        noteRequired
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <ConfirmDialog
        open={dialog.type === 'delete'}
        title="确认删除审核记录"
        description="仅未结算记录可删除；删除后会同步移除关联下载记录并回退任务领取数。"
        confirmLabel="删除"
        variant="danger"
        loading={deleting}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
